/**
 * Contract Fulfillment Store
 *
 * Tracks which API contracts have been successfully fulfilled in the
 * current booking session. Persisted to session storage so it survives
 * page refreshes.
 */
import { createEvent, createStore, sample } from "effector";
import { persist } from "effector-storage/session";
import { bookingProcessApi } from "../../../entities/booking-process";
import {
  $account,
  resetAccountForLocationChange,
} from "../../../entities/account";
import { $serviceLocation } from "../../../entities/locations";

export type FulfillmentState = Record<string, boolean>;

export const contractFulfilled = createEvent<string>();
export const resetFulfillment = createEvent();
/** Reset a single contract so it can re-fire on the next step submit. */
export const resetContract = createEvent<string>();

export const $contractFulfillment = createStore<FulfillmentState>({})
  .on(contractFulfilled, (state, contractId) => ({
    ...state,
    [contractId]: true,
  }))
  .on(resetContract, (state, contractId) => {
    const next = { ...state };
    delete next[contractId];
    return next;
  })
  .reset(resetFulfillment);

persist({
  store: $contractFulfillment,
  key: "booking-v4-contract-fulfillment",
});

// Reset contract fulfillment when the booking process resets (new session)
sample({
  clock: bookingProcessApi.reset,
  target: resetFulfillment,
});

// When serviceCheck returns a different location from where the account was
// created, reset accountCreate fulfillment so it re-fires at the new location.
sample({
  clock: resetAccountForLocationChange,
  fn: () => "accountCreate",
  target: resetContract,
});

// Reset serviceCheck when billingAddress.postal changes to a different value
// from the one that was last service-checked. This ensures that changing the
// postal (either on the get-started postal-input or on the address form) will
// always trigger a fresh service area validation on the next step submit.
sample({
  clock: $account,
  source: $serviceLocation,
  filter: (serviceLocation, account) => {
    const currentPostal = account.account?.billingAddress?.postal;
    const lastCheckedPostal = serviceLocation.lastCheckedPostal;
    // Only reset when we have both values and they differ
    return (
      !!currentPostal &&
      !!lastCheckedPostal &&
      currentPostal !== lastCheckedPostal
    );
  },
  fn: () => "serviceCheck",
  target: resetContract,
});
